import React, {useEffect, useState} from "react";
import {Button, Box, Popper} from "@mui/material";
import {commonButtonStyles} from "../../../utils/commonButtonStyles";
import {useTheme} from "../../../contexts/theme";
import CustomPopperComponent from "./CustomPopperComponent";
import {download} from "../../../utils/utils";
import {downloadEdit} from "../../../queries/editorQueries";

interface CustomDownloadButtonProps {
    text: string;
    disabled?: boolean;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
    width?: string;
    onFocus?: () => void;
    onBlur?: () => void;
    height?: string;
    marginLeft?: string;
    marginRight?: string;
    icon?: any;
    startIcon?: React.ReactNode;
    type?: 'button' | 'submit' | 'reset';
    color?: string;
    selected?: boolean;
    login?: boolean;
    additionalComponent?: React.ReactNode; // New prop for the additional component
    selectedClipsAspectRatio?: any;
    setIsClipDownloading: any;
    clips: any[];
    isProductionNeeded: boolean;
    editSessionId?: string;
    handlePopupClose?: any;
    editorDownload?: boolean;
}

const needButtonBackground = process.env.REACT_APP_SPORT === 'basketball'

const CustomDownloadButton = ({
                                  text,
                                  height,
                                  marginLeft,
                                  marginRight,
                                  icon,
                                  color = 'white',
                                  width,
                                  selected,
                                  login,
                                  additionalComponent,
                                  selectedClipsAspectRatio,
                                  setIsClipDownloading,
                                  clips,
                                  isProductionNeeded,
                                  editSessionId,
                                  handlePopupClose,
    editorDownload
                              }: CustomDownloadButtonProps) => {

    const theme = useTheme();
    const [downloadButtonPopperAnchorEl, setDownloadButtonPopperAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(downloadButtonPopperAnchorEl);
    const id = open ? 'download-button-popper' : undefined;
    const [disabled, setDisabled] = useState<boolean>(false);

    const handleClick = (event: any) => {
        setDownloadButtonPopperAnchorEl(downloadButtonPopperAnchorEl ? null : event.currentTarget);
    };

    const handleAdditionalComponentClick = (e: React.MouseEvent) => {
        // Stop propagation to prevent the Button's onClick from firing
        e.stopPropagation();
        handleClick(e);
    };

    const handleDownloadClip = async () => {
        try {
            if (editorDownload) setDisabled(true);
            setIsClipDownloading('DOWNLOAD')
            if(isProductionNeeded && editSessionId && editSessionId !== '') {
                const response = await downloadEdit(editSessionId, selectedClipsAspectRatio);
                handlePopupClose();
                await download(response.preview_url, clips[0].title, selectedClipsAspectRatio);
            } else {
                await Promise.all(
                    clips && clips.map(async (clipItem: any) => {
                        const url = clipItem.type === "clip" || clipItem.social_clip_url ? clipItem.social_clip_url : clipItem.preview_url;
                        const title = clipItem.title;
                        return download(url, title, selectedClipsAspectRatio);
                    })
                );
            }

            setIsClipDownloading('NODOWNLOAD')
        } catch (error){
            setIsClipDownloading('FAILED')
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                onClick={handleDownloadClip}
                startIcon={icon}
                disabled={disabled}
                sx={{
                    marginLeft: marginLeft || undefined,
                    marginRight: marginRight || undefined,
                    height: height || "40px",
                    width: width || 'auto',
                    ...commonButtonStyles(theme, {selected, login, color, needBackgroundColor: needButtonBackground}),
                }}
            >
                <Box sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                    {text}
                    {additionalComponent && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                            onClick={handleAdditionalComponentClick}
                        >
                            {additionalComponent}
                        </Box>
                    )}
                </Box>
            </Button>
            <Popper open={open} id={id} anchorEl={downloadButtonPopperAnchorEl} sx={{width: '300px', zIndex: 10000}}>
                <Box sx={{border: 1, p: 1, bgcolor: theme.palette.blue, width: '100%'}}>
                    <CustomPopperComponent setDownloadButtonPopperAnchorEl={setDownloadButtonPopperAnchorEl} handlePopupClose = {handlePopupClose} aspectRatio={selectedClipsAspectRatio} setIsClipDownloading={setIsClipDownloading} clips={clips} isProductionNeeded={isProductionNeeded} editSessionId={editSessionId}/>
                </Box>
            </Popper>
        </>
    );
};

export default CustomDownloadButton;
